import { useMutation } from '@apollo/client';
import { kebabCase, omit, orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHonorificTemplateBlockTimeValues } from '../../../../data/data/honorific-block-times/select-values';
import { AbortType, TemplateEntryItem } from '../../../../data/data/types';
import { isTemplateExceptionValidForAppointmentBlock } from '../../../../data/data/utils';
import {
  CreateTemplateItemDocument,
  GetTemplateItemsDocument,
  Honorific,
  TemplateEntryType,
  TemplateItemDataFragment,
} from '../../../../data/types/api/graphql';
import { formatTimeSpanToTime } from '../../../../data/utils/date.utils';
import { FormError } from '../../../../data/utils/form.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { TemplateEntryList } from '../../../../elements/template-entry-list/template-entry-list';
import { TemplateEntryForm } from '../../../../elements/template-enty-form/template-entry-form';
import { useTemplateForm } from '../../form/use-template-form';

type TemplateCreateModalProps = {
  onSaved?: (value: TemplateItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function TemplateCreateModal({
  onSaved,
  onCancel,
  onClose,
}: TemplateCreateModalProps) {
  const intl = useIntl();

  const { healthProviderId } = useAppContext();

  const [formStep, setFormStep] = useState<number>(1);

  const abortType = useRef<AbortType | null>(null);

  const { showAlertMessage } = useAppContext();

  // honorific block times for templates
  const {
    data: honorificBlockTimeItemsData,
    loading: honorificBlockTimeItemsLoading,
  } = useHonorificTemplateBlockTimeValues(healthProviderId);

  const availableHonoficBlockTimeItems = useMemo(
    () => honorificBlockTimeItemsData?.filter(x => x.value > 0) ?? [],
    [honorificBlockTimeItemsData]
  );

  const { form, formIsValid, setErrors } = useTemplateForm();

  const [entryFormDirty, setEntryFormDirty] = useState<boolean>(false);

  const onHandleEntryFormDirty = useCallback((dirty: boolean) => {
    setEntryFormDirty(dirty);
  }, []);

  const entries = form.watch('entries');
  const honorific = form.watch('honorific');
  const name = form.watch('name');

  useEffect(() => {
    if (!honorific && availableHonoficBlockTimeItems?.length > 0) {
      form.setValue('honorific', availableHonoficBlockTimeItems[0].key, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [availableHonoficBlockTimeItems, honorific, form]);

  const blockSize = useMemo(() => {
    if (honorific) {
      const honorificBlockTimeItem = availableHonoficBlockTimeItems.find(
        item => item.key === honorific
      );

      return honorificBlockTimeItem?.value ?? null;
    }

    return availableHonoficBlockTimeItems[0]?.value ?? null;
  }, [honorific, availableHonoficBlockTimeItems]);

  const onAddEntry = useCallback(
    (value: TemplateEntryItem) => {
      let newEntries = [...entries];

      if (value.type === TemplateEntryType.Exception) {
        newEntries = newEntries.map(option => {
          const appointments = [...option.appointments];

          if (isTemplateExceptionValidForAppointmentBlock(value, option)) {
            appointments.push(value);
          }

          return {
            ...option,
            appointments: orderBy(appointments, entry => [
              formatTimeSpanToTime(entry.minuteStartTime),
            ]),
          };
        });
      } else {
        newEntries.push({ ...value, appointments: [] });
      }

      form.setValue(
        'entries',
        orderBy(newEntries, entry =>
          formatTimeSpanToTime(entry.minuteStartTime)
        ),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [form, entries]
  );

  const onRemoveEntry = useCallback(
    (value: string) => {
      const newEntries = [...entries]
        .map(entry => ({
          ...entry,
          appointments: entry.appointments.filter(
            appointment => appointment.id !== value
          ),
        }))
        .filter(entry => entry.id !== value);

      form.setValue(
        'entries',
        orderBy(newEntries, entry =>
          formatTimeSpanToTime(entry.minuteStartTime)
        ),
        {
          shouldDirty: true,
        }
      );
    },
    [form, entries]
  );

  const [createTemplateItem, { loading: createTemplateItemLoading }] =
    useMutation(CreateTemplateItemDocument, {
      refetchQueries: [GetTemplateItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    if (honorific === null || honorific === undefined) return;
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await createTemplateItem({
          variables: {
            ...values,
            healthProviderId,
            honorific: values.honorific as Honorific,
            entries: values.entries.map(entry =>
              omit(
                {
                  ...entry,
                  id: entry.id.includes('temp-id') ? null : entry.id,
                  appointments: entry.appointments.map(appointment =>
                    omit(
                      {
                        ...appointment,
                        id: appointment.id.includes('temp-id')
                          ? null
                          : appointment.id,
                      },
                      '__typename',
                      'appointments'
                    )
                  ),
                },
                '__typename'
              )
            ),
          },
        });

        if (result.data?.templateCreate.template) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'template.template-create.success',
            }),
          });

          onSaved?.(result.data.templateCreate.template);

          onClose?.();
        } else {
          setErrors(result.data?.templateCreate?.errors);
          (result.data?.templateCreate?.errors as FormError[]).forEach(e => {
            showAlertMessage({
              type: AlertMessageType.ERROR,
              message: intl.formatMessage({
                id: `common.errors.${kebabCase(e.message)}`,
                defaultMessage: e.message,
              }),
            });
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    honorific,
    createTemplateItem,
    healthProviderId,
    showAlertMessage,
    intl,
    onSaved,
    onClose,
    setErrors,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const isDirty = form.formState.isDirty || entryFormDirty;

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (abortType.current === AbortType.Cancel) {
        if (!confirmed && isDirty) {
          setCancelPromptOpen(true);
        } else {
          onCancel?.();
          onClose?.();
        }

        return;
      }

      if (abortType.current === AbortType.Back) {
        if (!confirmed) {
          if (entries.length > 0) {
            setCancelPromptOpen(true);
          } else {
            setFormStep(1);
          }
        } else {
          form.setValue('entries', []);
          setFormStep(1);
          onCancel?.();
        }
      }
    },
    [entries.length, form, isDirty, onCancel, onClose]
  );

  const onNext = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const canDoNext = useMemo(() => {
    if (formStep === 1 && honorific && name) {
      return true;
    }
    return false;
  }, [formStep, honorific, name]);

  return (
    <>
      <Modal
        testId="template-create"
        title={<CustomFormattedMessage id="template.template-create.title" />}
        width={ModalWidth.LARGE}
        onClose={() => {
          abortType.current = AbortType.Cancel;
          onHandleCancel(false);
        }}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => {
                abortType.current = AbortType.Cancel;
                onHandleCancel(false);
              }}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            {formStep > 1 && (
              <Button
                inverted
                onClick={() => {
                  abortType.current = AbortType.Back;
                  onHandleCancel(false);
                }}
              >
                <CustomFormattedMessage id="common.previous" />
              </Button>
            )}

            {formStep < 2 ? (
              <Button
                disabled={!canDoNext}
                onClick={() => onNext(formStep + 1)}
              >
                <CustomFormattedMessage id="common.next" />
              </Button>
            ) : (
              <Button
                icon={
                  createTemplateItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={
                  !formIsValid ||
                  entryFormDirty ||
                  createTemplateItemLoading ||
                  honorificBlockTimeItemsLoading
                }
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="template.template-create.save" />
              </Button>
            )}
          </>
        }
      >
        <FormProvider {...form}>
          {formStep === 1 && honorificBlockTimeItemsData && (
            <>
              <FormRow>
                <InputFieldController
                  name="name"
                  label={
                    <>
                      <CustomFormattedMessage id="common.name" />
                      <small>*</small>
                    </>
                  }
                  placeholder={intl.formatMessage({ id: 'common.name' })}
                />
              </FormRow>
              <FormRow>
                <InputSelectController<Honorific>
                  name="honorific"
                  label={
                    <>
                      <CustomFormattedMessage id="common.honorific" />
                      <small>*</small>
                    </>
                  }
                  options={availableHonoficBlockTimeItems}
                />
              </FormRow>
            </>
          )}
        </FormProvider>
        {formStep === 2 && (
          <>
            <h3>{name}</h3>
            <div className="template-modal-info__honorific">
              <h4>
                <CustomFormattedMessage id="common.honorific" />
              </h4>
              <p>
                <CustomFormattedMessage
                  id={`common.honorific.${kebabCase(honorific ?? '')}`}
                />{' '}
                <span>
                  (
                  <CustomFormattedMessage
                    id="common.minimum-block-size"
                    values={{ minutes: blockSize }}
                  />
                  )
                </span>
              </p>
            </div>
            <h4>
              <CustomFormattedMessage id="template.template-detail" />
            </h4>
            {honorific && healthProviderId && (
              <TemplateEntryForm
                healthProviderId={healthProviderId}
                honorific={honorific}
                blockSize={blockSize ?? 0}
                entries={entries}
                onSave={onAddEntry}
                onDirty={onHandleEntryFormDirty}
              />
            )}

            <h4>
              <CustomFormattedMessage id="template.template-details" />
            </h4>
            <TemplateEntryList
              items={entries}
              actions={[
                {
                  icon: ButtonIcon.MINUS,
                  onClick: onRemoveEntry,
                },
              ]}
            />
          </>
        )}
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
